/**
 * Confetti burst on a win.
 *
 * Canvas-based rather than the original's approach of appending 80 absolutely
 * positioned divs to the document and letting CSS animations remove them --
 * which stacked up if wins landed close together. Respects
 * prefers-reduced-motion.
 */

import { useEffect, useRef } from 'react';

interface Props {
  /** Changing identity triggers a burst. Null does nothing. */
  trigger: { id: number } | null;
}

const PARTICLE_COUNT = 90;
const GRAVITY = 0.28;
const DRAG = 0.992;
const COLORS = ['#d4af37', '#f0d060', '#eef0f4', '#2fbf71', '#e5484d'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  spin: number;
  angle: number;
  life: number;
}

export function Confetti({ trigger }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (!trigger) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.scale(dpr, dpr);

    const width = window.innerWidth;
    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: width / 2 + (Math.random() - 0.5) * width * 0.5,
      y: -20 - Math.random() * 80,
      vx: (Math.random() - 0.5) * 6,
      vy: Math.random() * 3 + 1,
      size: Math.random() * 7 + 4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
      spin: (Math.random() - 0.5) * 0.3,
      angle: Math.random() * Math.PI,
      life: 1,
    }));

    const height = window.innerHeight;
    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      let alive = false;

      for (const p of particles) {
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        p.angle += p.spin;
        if (p.y > height * 0.72) p.life -= 0.02;
        if (p.life <= 0) continue;
        alive = true;

        ctx.save();
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      }

      if (alive) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };
    frameRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameRef.current);
  }, [trigger]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 90,
      }}
    />
  );
}
